"use client";

import React, { useRef, useEffect, useState, useCallback } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";

import products, { type Product } from "@/data/products-page";
import ProductCard from "./ProductCard";
import EnquiryModal from "./EnquiryModal";

gsap.registerPlugin(ScrollTrigger);

const MOBILE_BP = 768;

export default function ProductsStack() {
  const sectionRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [isMobile, setIsMobile] = useState(false);
  const [mounted, setMounted] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [activeProduct, setActiveProduct] = useState<Product | null>(null);

  const handleEnquire = useCallback((product: Product) => {
    setActiveProduct(product);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => setModalOpen(false), []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= MOBILE_BP);
    check();
    setMounted(true);
    window.addEventListener("resize", check, { passive: true });
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    const handleResize = () => ScrollTrigger.refresh();
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const setCardRef = useCallback((el: HTMLDivElement | null, i: number) => {
    cardRefs.current[i] = el;
  }, []);

  // ProductsStack.tsx — replace the useGSAP block

useGSAP(
  () => {
    if (!mounted || isMobile) return;

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add(`(min-width: ${MOBILE_BP + 1}px)`, () => {
        const cards = cardRefs.current.filter(
          (c): c is HTMLDivElement => c !== null
        );
        if (!cards.length || !trackRef.current) return;

        gsap.killTweensOf(cards);

        cards.forEach((card, i) => {
          const meta = card.querySelector("[data-animate='meta']");
          const title = card.querySelector("[data-animate='title']");
          const desc = card.querySelector("[data-animate='desc']");
          const features = card.querySelectorAll("[data-animate='feature']");
          const cta = card.querySelector("[data-animate='cta']");
          const imageInner = card.querySelector(
            "[data-image-inner]"
          ) as HTMLElement | null;

          const targets = [meta, title, desc, ...Array.from(features), cta].filter(
            Boolean
          ) as Element[];

          // ── FIRST CARD: visible immediately, no scroll-in needed ──
          if (i === 0) {
            gsap.set(targets, { opacity: 0, y: 30 });
            gsap.set(title, { opacity: 0, y: 60, skewY: 2 });

            const introTl = gsap.timeline({
              defaults: { ease: "power3.out", duration: 0.75 },
              delay: 0.2,
            });
            if (meta) introTl.to(meta, { opacity: 1, y: 0 }, 0);
            if (title) introTl.to(title, { opacity: 1, y: 0, skewY: 0 }, 0.05);
            if (desc) introTl.to(desc, { opacity: 0.65, y: 0 }, 0.15);
            if (features.length)
              introTl.to(features, { opacity: 1, y: 0, stagger: 0.07 }, 0.2);
            if (cta) introTl.to(cta, { opacity: 1, y: 0 }, 0.3);
          } else {
            // ── SUBSEQUENT CARDS: reveal as card scrolls INTO view ──
            // Set hidden state
            gsap.set(targets, { opacity: 0, y: 30 });
            if (title) gsap.set(title, { opacity: 0, y: 60, skewY: 2 });

            // The card sits at slot `i` in the track.
            // Its natural top is at `i * 100vh` within the track.
            // We want animation to START before it sticks — when the
            // PREVIOUS card is being scrolled past (roughly the last
            // 40% of the previous slot).
            const slotStart = () =>
              `top+=${(i - 0.4) * window.innerHeight} top`;
            const slotEnd = () =>
              `top+=${(i - 0.05) * window.innerHeight} top`;

            const revealTl = gsap.timeline({
              defaults: { ease: "power2.out", duration: 0.6 },
              paused: true,
            });
            if (meta) revealTl.to(meta, { opacity: 1, y: 0 }, 0);
            if (title)
              revealTl.to(title, { opacity: 1, y: 0, skewY: 0 }, 0.05);
            if (desc) revealTl.to(desc, { opacity: 0.65, y: 0 }, 0.1);
            if (features.length)
              revealTl.to(features, { opacity: 1, y: 0, stagger: 0.06 }, 0.15);
            if (cta) revealTl.to(cta, { opacity: 1, y: 0 }, 0.25);

            ScrollTrigger.create({
              trigger: trackRef.current,
              start: slotStart,
              end: slotEnd,
              invalidateOnRefresh: true,
              onEnter: () => revealTl.play(),
              onEnterBack: () => revealTl.play(),
              onLeaveBack: () => revealTl.pause(0),
              // Don't reset on onLeave — user has scrolled past, keep visible
            });
          }

          // ── Parallax image (unchanged conceptually) ──
          if (imageInner) {
            gsap.fromTo(
              imageInner,
              { yPercent: 0, scale: 1 },
              {
                yPercent: -8,
                scale: 1.07,
                ease: "none",
                scrollTrigger: {
                  trigger: trackRef.current,
                  start: () => `top+=${i * window.innerHeight} top`,
                  end: () => `top+=${(i + 1) * window.innerHeight} top`,
                  scrub: 1.5,
                  invalidateOnRefresh: true,
                },
              }
            );
          }
        });

        // ── Progress bar ──
        if (progressRef.current) {
          const bar = progressRef.current;
          ScrollTrigger.create({
            trigger: trackRef.current,
            start: "top top",
            end: "bottom bottom",
            scrub: true,
            onUpdate: (self) => {
              bar.style.width = `${self.progress * 100}%`;
              const idx = Math.min(
                Math.floor(self.progress * products.length),
                products.length - 1
              );
              bar.style.backgroundColor = products[idx].accent;
            },
          });
        }

        ScrollTrigger.refresh();
      });

      return () => mm.revert();
    }, sectionRef);

    return () => ctx.revert();
  },
  { 
    scope: sectionRef.current || undefined,
    dependencies: [mounted, isMobile]
  }
);
  // ── Inside ProductsStack.tsx ──

function buildTextEntrance(card: HTMLDivElement, immediate: boolean) {
  const D = immediate ? 0.6 : 0.55;       // Animation speed
  const delay = immediate ? 0.15 : 0.05;  // Start delay

  // Select elements
  const meta = card.querySelector("[data-animate='meta']");
  const title = card.querySelector("[data-animate='title']");
  const desc = card.querySelector("[data-animate='desc']");
  const features = card.querySelectorAll("[data-animate='feature']");
  const ctaWrapper = card.querySelector("[data-animate='cta']");
  
  // 🎯 Select the button specifically
  const btn = card.querySelector("[data-gsap-btn]") as HTMLElement | null;

  const tl = gsap.timeline({ paused: true, defaults: { ease: "power3.out", duration: D } });

  // 1. Standard text entrance
  if (meta) tl.fromTo(meta, { y: 25, opacity: 0 }, { y: 0, opacity: 1 }, delay);
  
  if (title) {
    tl.fromTo(title, { y: 70, skewY: 2.5, opacity: 0 }, { y: 0, skewY: 0, opacity: 1 }, "<0.08");
  }
  
  if (desc) {
    tl.fromTo(desc, { y: 30, opacity: 0 }, { y: 0, opacity: 0.65 }, "<0.1");
  }
  
  if (features.length) {
    tl.fromTo(features, { y: 22, opacity: 0 }, { y: 0, opacity: 1, stagger: 0.06 }, "<0.1");
  }

  // 2. CTA Container slides up
  if (ctaWrapper) {
     tl.fromTo(ctaWrapper, { y: 25, opacity: 0 }, { y: 0, opacity: 1 }, "<0.1");
  }

  // 3. 🌟 THE "GASPA" (Bouncy Pop)
  // Triggers right after container finishes moving
  if (btn) {
    tl.fromTo(
      btn,
      { 
        scale: 0.7,     // Start small
        opacity: 0,     // Start invisible
        rotationX: -10  // Slight 3D tilt start
      },
      { 
        scale: 1,       // Normal size
        opacity: 1,     // Fade in
        rotationX: 0,   // Level out
        duration: 0.6,  // Slightly longer for emphasis
        ease: "elastic.out(1, 0.5)" // Bouncy spring physics
      }, 
      "-=0.25"          // Overlap timing
    );
  }

  return tl;
}

  if (!mounted) return null;

  const trackHeight = isMobile ? undefined : `${products.length * 100}vh`;

  return (
    <>
      
      {!isMobile && (
        <div
          ref={progressRef}
          className="fixed left-0 top-0 z-[9990] h-[3px] w-0 rounded-r pointer-events-none transition-colors duration-500"
          style={{ backgroundColor: products[0].accent }}
          aria-hidden="true"
        />
      )}

      <section
        id="products"
        ref={sectionRef}
        className="relative w-full bg-[#f8f7f3]"
        aria-label="Products showcase"
      >
        <header className="relative z-[1] flex w-full flex-col items-start gap-6 bg-[#f8f7f3] px-[6vw] pb-10 pt-20 sm:flex-row sm:items-end sm:justify-between sm:px-[7vw] sm:pb-12 sm:pt-24">
          <div>
            <p className="mb-2.5 text-xs font-bold tracking-[0.28em] uppercase text-neutral-400">
              Our Collection
            </p>

            <h2 className="text-[clamp(2rem,4vw,3.5rem)] font-extrabold leading-[1.08] tracking-tight text-[#0a0a0a]">
              Premium
              <br />
              Home Collection
            </h2>
          </div>

          <div className="max-w-[38ch] text-left sm:text-right">
            <p className="text-base leading-[1.7] text-neutral-500">
              Discover our curated collection of premium furniture, home décor,
              garden essentials, kitchenware, and bathroom accessories—expertly
              crafted to bring timeless elegance, exceptional quality, and modern
              functionality to every living space.
            </p>
          </div>
        </header>

        <div ref={trackRef} className="relative w-full" style={{ height: trackHeight }}>
          {products.map((product, i) => (
            <div
              key={product.id}
              className="sticky top-0 h-[100vh] w-full overflow-hidden isolate max-md:static max-md:h-auto max-md:min-h-[100svh]"
              style={{ zIndex: i + 1, backgroundColor: product.bg }}
            >
              <ProductCard
                ref={(el) => setCardRef(el, i)}
                product={product}
                index={i}
                isMobile={isMobile}
                onEnquire={handleEnquire}
              />
            </div>
          ))}
        </div>
      </section>

      <EnquiryModal product={activeProduct} isOpen={modalOpen} onClose={handleCloseModal} />
    </>
  );
}